import { BLANK_DRIVEN_ANSWER, sourceOptionKey, type AnswerChoice } from '@agavi/blocks';

import type { RecalledHere } from './answer-ref-context';

/**
 * WHERE A RECALL CYCLE IS STANDING, and what it can step to.
 *
 * TWO CONTROLS STEP ONE ANSWER. The card cycles on a click — one press, forward, wrapping —
 * because the common source has two answers and a menu would be three interactions to do what
 * one does. The editor's driven box pages with chevrons, because there you are authoring a
 * question and an answer key per row and need to arrive at a named one deliberately.
 *
 * They move the same stand-in, so where the cycle is standing has to be computed once. Two
 * copies of this derivation is two chances to disagree about which answer is on screen, which
 * is the disagreement that cost this feature a second link control and a stale warning already.
 *
 * Only answers with a value to give are in it: an open class ("anything else") has none until
 * there is a control that takes a typed one, and landing on it would blank the sentence.
 */
export function recallCycle(row: RecalledHere): { usable: AnswerChoice[]; at: number } {
  const usable = row.choices.filter(c => c.sample !== null);
  /*
   * BEFORE THE FIRST PRESS the cycle is standing on the sampled correct answer, not on nothing.
   *
   * Reading `chosen` alone found nothing there and stepped to the first choice — which for most
   * sources IS that same correct answer, so the press changed nothing and the cycle appeared to
   * need two.
   */
  const current = row.chosen !== undefined ? row.chosen : row.resting;
  return { usable, at: usable.findIndex(c => JSON.stringify(c.sample) === JSON.stringify(current)) };
}

/**
 * THE ANSWER THAT LANDS THE DRIVER ON ONE ROW — the inverse of `drivenRowIdFor`.
 *
 * Undefined where no stand-in reaches it: a keyword's catch-all is "typed something nobody
 * listed", and the only honest stand-in for that is a word the teacher has not written.
 *
 * `BLANK_DRIVEN_ANSWER` for the BLANK row, which is why this exists at all. The cycle was
 * built from the driver's answer CHOICES, and "no answer" is not one of them — it is the
 * absence of one. So an MCQ with three options projected four versions and both controls
 * paged three, leaving the fourth authorable from nowhere. Reported as "why am i not seeing
 * No Answer in the sequence?".
 *
 * NOT `null`: the bench reads a null stand-in as "clear my pick", which falls back to the
 * sampled correct answer and lands on the first row again. An EMPTY answer is a different
 * statement from no stand-in at all, and only the empty one can be chosen.
 *
 * TWO WAYS TO MATCH, because the two kinds of driver key their rows differently.
 *
 * A BLOCK driver's row id IS the option id, which is also the choice's id, so they compare
 * directly. An ALIAS union row is an equivalence class and its id is `ur_text:swamp` — nothing
 * a choice carries. Comparing ids there matched NOTHING, so every option row looked unreachable
 * and the cycle collapsed to the one row that needs no choice at all: an alias-driven block
 * paged from its answer to "No answer" and could not get back. Found in the browser; no test
 * saw it because the database held no alias driver to build one from.
 *
 * So a union row is matched through its `sourceOptions`, which name the member options it is
 * made of — exactly addressed, because an option ID ALONE collides (`default-option-1` is a
 * literal two different blocks both use). `source` is the driver's own answer key, which
 * `RecalledHere.source` already carries in the same spelling.
 */
export function standInForDrivenRow(
  row: { id: string; kind: 'option' | 'other' | 'blank'; sourceOptions?: string[]; sample?: unknown },
  choices: AnswerChoice[],
  /** The driver's `taskId:blockId:facet`. Absent on a block driver, which needs no translation. */
  source?: string,
): { sample: unknown } | undefined {
  if (row.kind === 'blank') return { sample: BLANK_DRIVEN_ANSWER };
  /*
   * THE ROW'S OWN ANSWER FIRST, which is the only thing that reaches a union row contributed
   * by a member other than the alias's first. `choices` are one member's, so a row from
   * another member found nothing in them: a two-site alias projected six versions and the
   * editor could open four. The union attaches `sample` per row for exactly this.
   */
  if (row.sample !== undefined) return { sample: row.sample };
  const from = row.sourceOptions;
  const hit = from && source
    ? choices.find(c => from.includes(sourceOptionKey(source, c.id)))
    : choices.find(c => c.id === row.id);
  return hit && hit.sample !== null ? { sample: hit.sample } : undefined;
}

/**
 * THE ROWS BOTH CONTROLS PAGE, in the driver's own order.
 *
 * The editor's arrows and the card's one-press cycle move the same stand-in, so they must
 * agree on how many positions there are. Built once here rather than filtered at each: the
 * blank row is exactly the sort of member one of two copies drops, and a card offering three
 * where the box offers four is the disagreement this axis keeps producing.
 *
 * A row with no reachable stand-in is left out — a keyword's catch-all, which needs a word
 * the teacher has not written. A control that lands on a row it cannot select is worse than
 * one that does not offer it.
 */
export function reachableDrivenRows<
  R extends { id: string; kind: 'option' | 'other' | 'blank'; sourceOptions?: string[] },
>(rows: R[], choices: AnswerChoice[], source?: string): R[] {
  return rows.filter(r => standInForDrivenRow(r, choices, source) !== undefined);
}
